import { type JSX, useMemo } from 'react';

import borders from '@dynatrace/strato-design-tokens/borders';
import colors from '@dynatrace/strato-design-tokens/colors';

import * as styles from './DataTableGanttChartGrid.sty.js';
import { useScaleAndFormatter } from './hooks/useScaleAndFormatter.js';
import {
  getGanttHorizontalTimeAxisNumTicks,
  getHorizontalNumTicks,
} from '../../../../charts/core/utils/ticks.js';
import { getTickValueAsNumber } from '../../../../core/utils/get-tick-value-as-number.js';
import { getTicks } from '../../../../core/utils/get-ticks.js';
import type { DataTableGanttColumnConfig } from '../ColumnTypes/column-types-types.js';

type DataTableGanttChartGridProps = Readonly<{
  width: number;
  height: number;
  min: number;
  max: number;
  config: DataTableGanttColumnConfig;
  isSubrowIndicatorColumn: boolean;
}>;

/**
 * @internal
 */
export const DataTableGanttChartGrid = ({
  width,
  height,
  min,
  max,
  config,
  isSubrowIndicatorColumn,
}: DataTableGanttChartGridProps): JSX.Element => {
  const { xAxisType = 'numerical' } = config;
  const { scale } = useScaleAndFormatter(width, config, min, max);
  const numTicks =
    xAxisType === 'time'
      ? getGanttHorizontalTimeAxisNumTicks(width)
      : getHorizontalNumTicks(width);

  const lines = useMemo(() => {
    const ticks = getTicks(scale, numTicks) as number[];

    // if it's not the subrow indicator column,
    // and the first tick is the same as min, remove it, as the gantt column already draws a line at the start
    if (
      !isSubrowIndicatorColumn &&
      getTickValueAsNumber(ticks.at(0)?.valueOf()) === min
    ) {
      ticks.shift();
    }

    // if the last tick is the same as max remove it, as the gantt column already draws a line at the end
    if (getTickValueAsNumber(ticks.at(-1)?.valueOf()) === max) {
      ticks.pop();
    }

    return ticks.map((rawTick) => {
      const tick = Math.max(scale(rawTick), 0.5);
      return (
        <line
          key={+rawTick}
          x1={tick}
          x2={tick}
          y1={0}
          y2={height}
          strokeWidth={borders.Width.Default}
          stroke={colors.Border.Neutral.Default}
        />
      );
    });
  }, [scale, numTicks, min, max, height]);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      height={height}
      width={width}
      className={styles.ganttChartGrid}
    >
      {lines}
    </svg>
  );
};
